import React, { useState, useCallback, useMemo } from 'react';
import {
  useRefresh,
  useNotify,
  useGetList,
  useGetOne,
} from 'react-admin';
import Papa from 'papaparse';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';

import { getCurrencySymbol } from '@src/utils/currency';

import type { FacetGroup } from '@src/facets';
import type { PartnerSettings } from '@src/settings';
import type { PricingRuleInput } from './types';
import { pricingRulesDataProvider } from './dataProvider';
import { SOURCE_COLUMNS } from './PricingRulesForm';

interface ParsedRule extends Partial<PricingRuleInput> {
  _rowIndex: number;
  _hasError?: boolean;
  _errorMessage?: string;
}

interface PricingRulesImportDialogProps {
  open: boolean;
  onClose: () => void;
}

const HEADER_MAP: Record<string, keyof PricingRuleInput> = {
  source_column: 'sourceColumn',
  department: 'department',
  category: 'category',
  merchant_category: 'merchantCategory',
  upc: 'upc',
  primary_tag: 'tag',
  primary_name: 'name',
  secondary_tag: 'secondaryTag',
  secondary_name: 'secondaryName',
  tertiary_tag: 'tertiaryTag',
  tertiary_name: 'tertiaryName',
  discount: 'discount',
  condition: 'condition',
};

function parseTsvFile(content: string): { rules: ParsedRule[], errors: string[] } {
  const result = Papa.parse<Record<string, string>>(content, {
    delimiter: '\t',
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.toLowerCase().trim(),
  });

  if (result.data.length === 0) {
    return { rules: [], errors: ['File must have a header row and at least one data row'] };
  }

  const rules: ParsedRule[] = [];
  const errors: string[] = [];

  result.data.forEach((row, index) => {
    const rowIndex = index + 2; // +2 because: 0-indexed + header row
    const rule: ParsedRule = { _rowIndex: rowIndex };

    // Map CSV columns to rule fields
    Object.entries(row).forEach(([header, value]) => {
      const fieldName = HEADER_MAP[header];
      if (fieldName && value !== undefined && value.trim() !== '') {
        (rule as unknown as Record<string, unknown>)[fieldName] = value.trim();
      }
    });

    // Parse discount as number
    if (rule.discount !== undefined) {
      const discount = parseFloat(String(rule.discount));
      if (Number.isNaN(discount)) {
        rule._hasError = true;
        rule._errorMessage = `Invalid discount value: ${rule.discount}`;
        errors.push(`Row ${rowIndex}: Invalid discount value`);
      } else {
        rule.discount = discount;
      }
    } else {
      rule._hasError = true;
      rule._errorMessage = 'Missing discount value';
      errors.push(`Row ${rowIndex}: Missing discount value`);
    }

    // Default source column
    if (!rule.sourceColumn) {
      rule.sourceColumn = 'price';
    }

    rules.push(rule);
  });

  // Add any parsing errors from papaparse
  result.errors.forEach((err) => {
    const rowNum = err.row !== undefined ? err.row + 2 : '?';
    errors.push(`Row ${rowNum}: ${err.message}`);
  });

  return { rules, errors };
}

function validateRules(
  rules: ParsedRule[],
  facetGroups: FacetGroup[],
): { rules: ParsedRule[], errors: string[] } {
  const validDepartments = facetGroups.find(fg => fg.tag === 'department')?.values.map(v => v.name) || [];
  const validCategories = facetGroups.find(fg => fg.tag === 'category')?.values.map(v => v.name) || [];
  const validTags = facetGroups.map(fg => fg.tag);

  // Build name map: tag -> valid names
  const nameMap: Record<string, string[]> = {};
  facetGroups.forEach((fg) => {
    nameMap[fg.tag] = fg.values.map(v => v.name);
  });

  const validatedRules: ParsedRule[] = [];
  const errors: string[] = [];

  rules.forEach((rule) => {
    const ruleErrors: string[] = [];

    // Skip if already has error
    if (rule._hasError) {
      validatedRules.push(rule);
      return;
    }

    // Validate department
    if (rule.department && validDepartments.length > 0 && !validDepartments.includes(rule.department)) {
      ruleErrors.push(`Invalid department: ${rule.department}`);
    }

    // Validate category
    if (rule.category && validCategories.length > 0 && !validCategories.includes(rule.category)) {
      ruleErrors.push(`Invalid category: ${rule.category}`);
    }

    // Validate tag
    if (rule.tag && validTags.length > 0 && !validTags.includes(rule.tag)) {
      ruleErrors.push(`Invalid tag: ${rule.tag}`);
    }

    // Validate name matches tag
    if (rule.name && rule.tag) {
      const validNames = nameMap[rule.tag] || [];
      if (validNames.length > 0 && !validNames.includes(rule.name)) {
        ruleErrors.push(`Invalid name "${rule.name}" for tag "${rule.tag}"`);
      }
    }

    // Validate secondary tag
    if (rule.secondaryTag && validTags.length > 0 && !validTags.includes(rule.secondaryTag)) {
      ruleErrors.push(`Invalid secondaryTag: ${rule.secondaryTag}`);
    }

    // Validate secondary name matches secondary tag
    if (rule.secondaryName && rule.secondaryTag) {
      const validNames = nameMap[rule.secondaryTag] || [];
      if (validNames.length > 0 && !validNames.includes(rule.secondaryName)) {
        ruleErrors.push(`Invalid secondaryName "${rule.secondaryName}" for secondaryTag "${rule.secondaryTag}"`);
      }
    }

    // Validate tertiary tag
    if (rule.tertiaryTag && validTags.length > 0 && !validTags.includes(rule.tertiaryTag)) {
      ruleErrors.push(`Invalid tertiaryTag: ${rule.tertiaryTag}`);
    }

    // Validate tertiary name matches tertiary tag
    if (rule.tertiaryName && rule.tertiaryTag) {
      const validNames = nameMap[rule.tertiaryTag] || [];
      if (validNames.length > 0 && !validNames.includes(rule.tertiaryName)) {
        ruleErrors.push(`Invalid tertiaryName "${rule.tertiaryName}" for tertiaryTag "${rule.tertiaryTag}"`);
      }
    }

    // Validate source column
    if (rule.sourceColumn && !(SOURCE_COLUMNS as readonly string[]).includes(rule.sourceColumn)) {
      ruleErrors.push(`Invalid sourceColumn: ${rule.sourceColumn}`);
    }

    const validatedRule = { ...rule };
    if (ruleErrors.length > 0) {
      validatedRule._hasError = true;
      validatedRule._errorMessage = ruleErrors.join('; ');
      errors.push(`Row ${rule._rowIndex}: ${ruleErrors.join('; ')}`);
    }
    validatedRules.push(validatedRule);
  });

  return { rules: validatedRules, errors };
}

type ImportPhase = 'idle' | 'starting' | 'importing' | 'complete';

const PricingRulesImportDialog = ({ open, onClose }: PricingRulesImportDialogProps) => {
  const refresh = useRefresh();
  const notify = useNotify();
  const { data: facetGroups = [] } = useGetList<FacetGroup>('facets');

  const [file, setFile] = useState<File | null>(null);
  const [parsedRules, setParsedRules] = useState<ParsedRule[]>([]);
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const [importPhase, setImportPhase] = useState<ImportPhase>('idle');
  const [importResult, setImportResult] = useState<{ created: number, errors: number }>({ created: 0, errors: 0 });

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      const { rules: parsed } = parseTsvFile(content);
      const { rules: validated } = validateRules(parsed, facetGroups);
      setParsedRules(validated);
    };
    reader.readAsText(selectedFile);
  }, [facetGroups]);

  const handleClose = useCallback(() => {
    setFile(null);
    setParsedRules([]);
    setShowErrorsOnly(false);
    setImportPhase('idle');
    setImportResult({ created: 0, errors: 0 });
    onClose();
  }, [onClose]);

  const handleImport = useCallback(async () => {
    const validRules = parsedRules
      .filter(r => !r._hasError && r.sourceColumn)
      .map((r) => {
        const cleanRule: PricingRuleInput = {
          sourceColumn: r.sourceColumn!, // Guaranteed by filter above
        };
        if (r.discount !== undefined) {
          cleanRule.discount = r.discount;
        }
        if (r.department) cleanRule.department = r.department;
        if (r.category) cleanRule.category = r.category;
        if (r.merchantCategory) cleanRule.merchantCategory = r.merchantCategory;
        if (r.tag) cleanRule.tag = r.tag;
        if (r.name) cleanRule.name = r.name;
        if (r.secondaryTag) cleanRule.secondaryTag = r.secondaryTag;
        if (r.secondaryName) cleanRule.secondaryName = r.secondaryName;
        if (r.tertiaryTag) cleanRule.tertiaryTag = r.tertiaryTag;
        if (r.tertiaryName) cleanRule.tertiaryName = r.tertiaryName;
        if (r.upc) cleanRule.upc = r.upc;
        if (r.condition) cleanRule.condition = r.condition;
        return cleanRule;
      });

    if (validRules.length === 0) {
      notify('No valid rules to import', { type: 'warning' });
      return;
    }

    try {
      // Phase 1: Start import session (creates new PricingDiscount)
      setImportPhase('starting');

      const startResult = await pricingRulesDataProvider.startImport('pricingRules', {});
      const { pricingDiscountId } = startResult.data;

      // Phase 2: Add rules to the import session
      setImportPhase('importing');

      const result = await pricingRulesDataProvider.addToImport('pricingRules', {
        pricingDiscountId,
        data: validRules,
      });

      const createdCount = result.created ?? 0;
      const errorCount = result.errors?.length ?? 0;

      setImportPhase('complete');
      setImportResult({ created: createdCount, errors: errorCount });

      if (createdCount > 0) {
        notify(`Imported ${createdCount} pricing rules`, { type: 'success' });
        refresh();
      }
      if (errorCount > 0) {
        notify(`${errorCount} rules failed to import`, { type: 'warning' });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Import failed';
      notify(`Error: ${message}`, { type: 'error' });
      setImportPhase('idle');
    }
  }, [parsedRules, notify, refresh]);

  const validRulesCount = parsedRules.filter(r => !r._hasError).length;
  const invalidRulesCount = parsedRules.length - validRulesCount;

  const displayedRules = useMemo(() => {
    if (showErrorsOnly) {
      return parsedRules.filter(r => r._hasError);
    }
    return parsedRules.slice(0, 100); // Limit preview to 100 rows
  }, [parsedRules, showErrorsOnly]);

  const { data: settings } = useGetOne<PartnerSettings & { id: number }>('settings', { id: 0 });
  const currencySymbol = getCurrencySymbol(settings?.primaryCurrencyCode);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Import Pricing Rules</DialogTitle>
      <DialogContent>
        {/* Step 1: File Upload */}
        {!file && importPhase === 'idle' && (
          <Box sx={{ py: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>Import Pricing Rules</AlertTitle>
              Importing will create a new pricing rule set. Once imported, you can initiate
              pricing to apply these rules to your catalog.
            </Alert>
            <Typography variant="body1" gutterBottom>
              Upload a TSV file with columns:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              source_column, department, category, merchant_category, upc, primary_tag, primary_name,
              secondary_tag, secondary_name, tertiary_tag, tertiary_name, discount
            </Typography>
            <Button variant="contained" component="label">
              Choose File
              <input type="file" hidden accept=".tsv,.txt,.csv" onChange={handleFileChange} />
            </Button>
          </Box>
        )}

        {/* Step 2: Preview */}
        {file && parsedRules.length > 0 && importPhase === 'idle' && (
          <Box sx={{ py: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Ready to Import</AlertTitle>
              Clicking &quot;Import&quot; will create a new pricing rule set with
              the {validRulesCount} valid rules from your file.
            </Alert>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h6">Preview</Typography>
              <Chip label={`${validRulesCount} valid`} color="success" size="small" />
              {invalidRulesCount > 0 && (
                <Chip
                  label={`${invalidRulesCount} invalid`}
                  color="error"
                  size="small"
                  onClick={() => setShowErrorsOnly(!showErrorsOnly)}
                  clickable
                />
              )}
              <Typography variant="body2" color="text.secondary">
                {file.name}
              </Typography>
            </Box>

            {invalidRulesCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Validation Issues</AlertTitle>
                {invalidRulesCount}
                {' '}
                rules have errors and will be skipped during import.
                Click the &quot;invalid&quot; chip above to view only problematic rows.
              </Alert>
            )}

            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Row</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Dept</TableCell>
                    <TableCell>Cat</TableCell>
                    <TableCell>Tag</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Secondary Tag</TableCell>
                    <TableCell>Secondary Name</TableCell>
                    <TableCell>Tertiary Tag</TableCell>
                    <TableCell>Tertiary Name</TableCell>
                    <TableCell>Discount / Price</TableCell>
                    <TableCell>Errors</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedRules.map((rule, idx) => {
                    let formattedDiscount = '-';
                    if (rule.discount != null) {
                      formattedDiscount = rule.sourceColumn === 'absolute_price'
                        ? `${currencySymbol}${(rule.discount / 100).toFixed(2)}`
                        : `${currencySymbol}${rule.discount}%`;
                    }
                    return (
                      <TableRow
                        key={idx}
                        sx={{
                          backgroundColor: rule._hasError ? 'rgba(255, 0, 0, 0.05)' : undefined,
                        }}
                      >
                        <TableCell>{rule._rowIndex}</TableCell>
                        <TableCell>{rule.sourceColumn}</TableCell>
                        <TableCell>{rule.department || '-'}</TableCell>
                        <TableCell>{rule.category || '-'}</TableCell>
                        <TableCell>{rule.tag || '-'}</TableCell>
                        <TableCell>{rule.name || '-'}</TableCell>
                        <TableCell>{rule.secondaryTag || '-'}</TableCell>
                        <TableCell>{rule.secondaryName || '-'}</TableCell>
                        <TableCell>{rule.tertiaryTag || '-'}</TableCell>
                        <TableCell>{rule.tertiaryName || '-'}</TableCell>
                        <TableCell>{formattedDiscount}</TableCell>
                        <TableCell>
                          {rule._errorMessage && (
                            <Tooltip title={rule._errorMessage}>
                              <Typography color="error" variant="caption" sx={{ cursor: 'help' }}>
                                {rule._errorMessage.slice(0, 30)}
                                ...
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {parsedRules.length > 100 && !showErrorsOnly && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Showing first 100 of
                {' '}
                {parsedRules.length}
                {' '}
                rows
              </Typography>
            )}
          </Box>
        )}

        {/* Import Progress */}
        {(importPhase === 'starting' || importPhase === 'importing') && (
          <Box sx={{ py: 4 }}>
            <Typography variant="h6" gutterBottom>Importing rules...</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flexGrow: 1 }}>
                <LinearProgress variant="indeterminate" />
              </Box>
            </Box>
          </Box>
        )}

        {/* Step 4: Import Complete */}
        {importPhase === 'complete' && (
          <Box sx={{ py: 4 }}>
            <Alert
              severity={importResult.errors > 0 ? 'warning' : 'success'}
              sx={{ mb: 2 }}
            >
              <AlertTitle>Import Complete</AlertTitle>
              <Box>
                Successfully imported
                {' '}
                {importResult.created}
                {' '}
                pricing rules.
              </Box>
              {importResult.errors > 0 && (
                <Box sx={{ mt: 1 }}>
                  {importResult.errors}
                  {' '}
                  rules failed to import.
                </Box>
              )}
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importPhase === 'starting' || importPhase === 'importing'}>
          Close
        </Button>
        {file && importPhase === 'idle' && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleImport}
            disabled={validRulesCount === 0}
          >
            Import
            {' '}
            {validRulesCount}
            {' '}
            Rules
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PricingRulesImportDialog;
